import React from "react";
import { COMPANY } from "../mock";
import { ShieldCheck } from "lucide-react";

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="font-serif-display text-xl md:text-2xl font-bold gpm-text-green mb-3">{title}</h2>
    <div className="text-sm md:text-base text-gray-700 leading-relaxed space-y-3">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  const updated = "June 2026";
  return (
    <div data-testid="privacy-policy-page">
      <div className="gpm-bg-green py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 gpm-text-gold mb-3"><ShieldCheck size={22} /><span className="text-xs uppercase tracking-widest">Your privacy matters</span></div>
          <h1 className="font-serif-display text-4xl md:text-5xl font-bold gpm-text-cream">Privacy Policy</h1>
          <p className="gpm-text-cream opacity-80 mt-3 text-sm">Last updated: {updated}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-14">
        <Section title="Introduction">
          <p>{COMPANY.name} ("we", "us", "our") is committed to protecting your privacy. This policy explains what personal information we collect when you use our website, how we use it, and the rights you have. By using this website or contacting us, you agree to the terms of this policy.</p>
        </Section>

        <Section title="Who we are">
          <p>{COMPANY.name} is a dealer in classic and modern classic motorcars, {COMPANY.address}. If you have any questions about this policy or your personal data, you can contact us at:</p>
          <ul className="list-disc pl-6">
            <li>Email: <a href={`mailto:${COMPANY.email}`} className="gpm-text-green underline">{COMPANY.email}</a></li>
            <li>Phone: <a href={`tel:${COMPANY.phone}`} className="gpm-text-green underline">{COMPANY.phone}</a></li>
          </ul>
        </Section>

        <Section title="Information we collect">
          <p>We only collect the information you choose to give us. When you submit an enquiry, contact form, or car sourcing request, we may collect:</p>
          <ul className="list-disc pl-6">
            <li>Your name</li>
            <li>Your email address</li>
            <li>Your telephone number</li>
            <li>Details of the vehicle you are interested in or looking for</li>
            <li>Any message or information you include in your enquiry</li>
          </ul>
          <p>We may also collect limited technical information automatically (such as your browser type and general usage data) to help the website function and improve.</p>
        </Section>

        <Section title="How we use your information">
          <p>We use your information to:</p>
          <ul className="list-disc pl-6">
            <li>Respond to your enquiries and provide the information or service you requested</li>
            <li>Contact you about vehicles, sourcing requests, or your enquiry</li>
            <li>Send you a confirmation that we have received your message</li>
            <li>Maintain records of enquiries and improve our service</li>
          </ul>
          <p>We do not sell your personal information to third parties.</p>
        </Section>

        <Section title="Social media and advertising">
          <p>We maintain a presence on Facebook and Instagram. If we run advertising through Meta (Facebook/Instagram), aggregated and anonymised performance data may be processed by Meta in accordance with their own privacy policy. We do not share your enquiry details with social platforms for advertising purposes.</p>
        </Section>

        <Section title="How we store and protect your data">
          <p>Your information is stored securely and access is limited to authorised members of our team. We keep your personal data only for as long as necessary to deal with your enquiry and for our legitimate business records.</p>
        </Section>

        <Section title="Sharing your information">
          <p>We may share your information with trusted service providers who help us operate our website and communicate with you (for example, our email provider). These providers are only permitted to use your data to provide services to us.</p>
        </Section>

        <Section title="Your rights">
          <p>Under UK data protection law (UK GDPR), you have the right to:</p>
          <ul className="list-disc pl-6">
            <li>Request a copy of the personal data we hold about you</li>
            <li>Ask us to correct or update your information</li>
            <li>Ask us to delete your information</li>
            <li>Object to or restrict how we use your information</li>
          </ul>
          <p>To exercise any of these rights, please contact us at <a href={`mailto:${COMPANY.email}`} className="gpm-text-green underline">{COMPANY.email}</a>.</p>
        </Section>

        <Section title="Cookies">
          <p>Our website may use essential cookies to function correctly. You can control or delete cookies through your browser settings. Disabling some cookies may affect how the website works.</p>
        </Section>

        <Section title="Changes to this policy">
          <p>We may update this privacy policy from time to time. Any changes will be posted on this page with a revised "last updated" date.</p>
        </Section>

        <Section title="Contact us">
          <p>If you have any questions or concerns about this privacy policy or how we handle your data, please contact us at <a href={`mailto:${COMPANY.email}`} className="gpm-text-green underline">{COMPANY.email}</a> or {COMPANY.phone}.</p>
        </Section>

        <p className="text-xs text-gray-400 border-t border-black/10 pt-6">This privacy policy is provided as a general template for {COMPANY.name}. For full legal compliance we recommend having it reviewed by a qualified professional.</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
